import React, { useEffect, useState } from 'react';
import { X, CheckCircle, Calendar, ExternalLink, Eye, Heart, Clock, User, Hash, Globe, Lock, Users } from 'lucide-react';
import ApprovalStatusBadge from '../shared/approval-status-badge';

const ACCENT = '#9C27B0';

const formatNumber = (n) => {
  if (n == null) return '0';
  return n.toLocaleString('vi-VN');
};

const formatDuration = (seconds) => {
  if (!seconds) return null;
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
};

const privacyIcon = (value) => {
  switch (value) {
    case 'EVERYONE': return <Globe size={11} className="text-green-500" />;
    case 'FRIENDS': return <Users size={11} className="text-blue-500" />;
    default: return <Lock size={11} className="text-gray-400" />;
  }
};

const privacyLabel = (value) => {
  switch (value) {
    case 'EVERYONE': return 'Công khai';
    case 'FRIENDS': return 'Bạn bè';
    case 'SELF': return 'Chỉ mình tôi';
    default: return value || 'Không rõ';
  }
};

const ReelsAutoCheckDetailModal = ({ reel, onClose }) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (reel) {
      const t = setTimeout(() => setVisible(true), 150);
      return () => clearTimeout(t);
    }
    setVisible(false);
  }, [reel]);

  if (!reel) return null;

  const hasTime = reel.time && !isNaN(new Date(reel.time));
  const d = hasTime ? new Date(reel.time) : null;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-3 animate-fade-in" onClick={onClose}>
      <style>{`
        @keyframes pop-in {
          0% { transform: scale(0.85); opacity: 0; }
          60% { transform: scale(1.03); opacity: 1; }
          100% { transform: scale(1); opacity: 1; }
        }
        .pop-in { animation: pop-in 0.4s cubic-bezier(.36,.07,.19,.97) forwards; }
        @keyframes glow-pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(156,39,176,0); }
          50% { box-shadow: 0 0 0 6px rgba(156,39,176,0.1); }
        }
        .glow-pulse { animation: glow-pulse 2.5s ease-in-out infinite; }
      `}</style>

      <div
        className="bg-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-hidden flex flex-col shadow-2xl animate-slide-up"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100" style={{ background: 'linear-gradient(135deg, #f3e8ff 0%, #fce7f3 50%, #fff 100%)' }}>
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-bold" style={{ color: ACCENT }}>Chi tiết Reel</h3>
          </div>
          <button onClick={onClose} className="w-7 h-7 rounded-full bg-white/80 flex items-center justify-center hover:bg-white">
            <X size={14} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {/* Thumbnail */}
          {reel.thumbnailUrl && (
            <div className="bg-black">
              <img src={reel.thumbnailUrl} alt="" className="w-full max-h-52 object-contain mx-auto" />
            </div>
          )}

          {/* ──── THÔNG TIN REEL ──── */}
          <div className="px-4 pt-4 pb-3">
            <div className="flex items-center gap-2 mb-3">
              <div className="h-px flex-1 bg-gradient-to-r from-purple-200 to-transparent" />
              <span className="text-[10px] font-bold text-purple-400 uppercase tracking-widest">Thông tin Reel</span>
              <div className="h-px flex-1 bg-gradient-to-l from-purple-200 to-transparent" />
            </div>

            <div className="space-y-2">
              {/* Author */}
              {(reel.facebookAuthorName || reel.facebookUsername) && (
                <div className="flex items-center gap-2">
                  <User size={13} className="text-purple-500 flex-shrink-0" />
                  <span className="text-xs text-gray-500">Người đăng:</span>
                  {reel.facebookAuthorId ? (
                    <a href={`https://facebook.com/${reel.facebookAuthorId}`} target="_blank" rel="noopener noreferrer"
                      className="text-xs font-bold text-blue-600 underline underline-offset-2">
                      {reel.facebookAuthorName || reel.facebookUsername}
                    </a>
                  ) : (
                    <span className="text-xs font-bold text-gray-800">{reel.facebookAuthorName || reel.facebookUsername}</span>
                  )}
                </div>
              )}

              {/* Date */}
              {d && (
                <div className={`rounded-xl overflow-hidden transition-all duration-500 ${visible ? 'pop-in glow-pulse' : 'opacity-0'}`}
                  style={{ background: 'linear-gradient(135deg, #f3e8ff 0%, #fce7f3 100%)', border: '1px solid #e9d5ff' }}>
                  <div className="px-3 py-2.5 flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: ACCENT }}>
                      <Calendar size={14} className="text-white" />
                    </div>
                    <div>
                      <p className="text-lg font-black" style={{ color: ACCENT }}>
                        {d.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                      </p>
                      <p className="text-[11px] text-purple-500 font-medium">
                        {d.toLocaleString('vi-VN', { weekday: 'long', hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Duration */}
              {reel.duration > 0 && (
                <div className="flex items-center gap-2">
                  <Clock size={13} className="text-purple-500 flex-shrink-0" />
                  <span className="text-xs text-gray-500">Thời lượng:</span>
                  <span className="text-xs font-bold text-gray-800">{formatDuration(reel.duration)}</span>
                </div>
              )}

              {/* Privacy */}
              {reel.privacyValue && (
                <div className="flex items-center gap-2">
                  {privacyIcon(reel.privacyValue)}
                  <span className="text-xs text-gray-500">Chế độ:</span>
                  <span className="text-xs font-bold text-gray-800">{privacyLabel(reel.privacyValue)}</span>
                </div>
              )}

              {/* Post ID */}
              {reel.postId && (
                <div className="flex items-center gap-2">
                  <Hash size={13} className="text-gray-400 flex-shrink-0" />
                  <span className="text-xs text-gray-500">Post ID:</span>
                  <span className="text-[10px] font-mono text-gray-500">{reel.postId}</span>
                </div>
              )}
            </div>
          </div>

          {/* ──── SỐ LIỆU ──── */}
          <div className="px-4 pb-2">
            <div className="flex items-center gap-2 mb-3">
              <div className="h-px flex-1 bg-gradient-to-r from-blue-200 to-transparent" />
              <span className="text-[10px] font-bold text-blue-400 uppercase tracking-widest">Số liệu</span>
              <div className="h-px flex-1 bg-gradient-to-l from-blue-200 to-transparent" />
            </div>

            <div className="grid grid-cols-3 gap-2">
              <div className="bg-blue-50 rounded-xl px-2 py-2.5 text-center">
                <Eye size={16} className="text-blue-500 mx-auto mb-1" />
                <p className="text-lg font-black text-blue-700">{formatNumber(reel.views)}</p>
                <p className="text-[10px] text-blue-400 font-medium">Lượt xem</p>
              </div>
              <div className="bg-purple-50 rounded-xl px-2 py-2.5 text-center">
                <span className="text-base block mb-1">❤️🩷😂</span>
                <p className="text-lg font-black text-purple-700">{formatNumber(reel.reactionsTotal)}</p>
                <p className="text-[10px] text-purple-400 font-semibold">Tương tác</p>
              </div>
              <div className="bg-pink-50 rounded-xl px-2 py-2.5 text-center">
                <Heart size={16} className="text-pink-500 mx-auto mb-1" />
                <p className="text-lg font-black text-pink-700">{formatNumber(reel.likes)}</p>
                <p className="text-[10px] text-pink-400 font-medium">Lượt thích</p>
              </div>
            </div>
            <p className="text-[9px] text-gray-400 text-center mt-1.5 mb-1">* Tương tác gồm: ❤️ 🩷 😂 😢 😡 👍 và các cảm xúc khác</p>
          </div>

          {/* ──── NỘI DUNG ──── */}
          {reel.text && (
            <div className="px-4 pb-3">
              <div className="flex items-center gap-2 mb-2">
                <div className="h-px flex-1 bg-gradient-to-r from-gray-200 to-transparent" />
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Nội dung</span>
                <div className="h-px flex-1 bg-gradient-to-l from-gray-200 to-transparent" />
              </div>
              <div className="bg-gray-50 rounded-xl px-3 py-2.5">
                <p className="text-xs text-gray-700 leading-relaxed whitespace-pre-line">{reel.text}</p>
              </div>
            </div>
          )}

          {/* ──── BÌNH LUẬN TIÊU BIỂU ──── */}
          {reel.topComments && reel.topComments.length > 0 && (
            <div className="px-4 pb-3">
              <div className="flex items-center gap-2 mb-2">
                <div className="h-px flex-1 bg-gradient-to-r from-amber-200 to-transparent" />
                <span className="text-[10px] font-bold text-amber-400 uppercase tracking-widest">Bình luận tiêu biểu</span>
                <div className="h-px flex-1 bg-gradient-to-l from-amber-200 to-transparent" />
              </div>
              <div className="space-y-1.5">
                {reel.topComments.slice(0, 5).map((c, i) => (
                  <div key={i} className="bg-amber-50/60 rounded-xl px-3 py-2 border border-amber-100/50">
                    <div className="flex items-start gap-2">
                      <div className="w-5 h-5 rounded-full bg-amber-200 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-[8px] font-bold text-amber-700">{(c.from?.name || '?')[0]}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="text-[10px] font-bold text-gray-700">{c.from?.name || 'Ẩn danh'}</span>
                        <p className="text-[11px] text-gray-600 leading-relaxed">{c.message}</p>
                        {c.likeCount > 0 && (
                          <span className="text-[9px] text-gray-400 flex items-center gap-0.5 mt-0.5">
                            <Heart size={7} /> {c.likeCount}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ──── TRẠNG THÁI ──── */}
          <div className="px-4 pb-3">
            <div className="flex items-center gap-2 mb-2">
              <div className="h-px flex-1 bg-gradient-to-r from-green-200 to-transparent" />
              <span className="text-[10px] font-bold text-green-400 uppercase tracking-widest">Trạng thái</span>
              <div className="h-px flex-1 bg-gradient-to-l from-green-200 to-transparent" />
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <ApprovalStatusBadge status={reel.companyApproved || 'pending'} />
            </div>
            {reel.companyApproved === 'rejected' && reel.notes && (
              <div className="mt-2 rounded-xl px-3 py-2 bg-red-50 border border-red-100">
                <p className="text-[11px] text-red-700 leading-relaxed font-medium">💬 Ghi chú từ Admin: {reel.notes}</p>
              </div>
            )}
          </div>

          {/* Motivational */}
          <div className="mx-4 mb-4 rounded-xl px-3 py-2.5" style={{ backgroundColor: '#fdf4ff', border: '1px solid #e9d5ff' }}>
            <p className="text-[11px] text-purple-700 leading-relaxed text-center font-medium">
              💜 Công ty ghi nhận sự nỗ lực của bạn — hãy cố gắng chăm chỉ mỗi ngày!
            </p>
          </div>
        </div>

        {/* Footer */}
        {(reel.videoUrl || reel.permalinkUrl) && (
          <div className="px-4 py-3 border-t border-gray-100">
            <a
              href={reel.videoUrl || `https://facebook.com${reel.permalinkUrl}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl text-sm font-bold border border-gray-200 text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <ExternalLink size={14} />
              Xem video trên Facebook
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReelsAutoCheckDetailModal;
